import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './client';

// Auth
export const fetchCaptcha = () => apiGet('/auth/captcha');
export const login = (body) => apiPost('/auth/login', body);
export const register = (body) => apiPost('/auth/register', body);
export const fetchMe = () => apiGet('/auth/me');

// Companies / FY
export const fetchFinancialYears = (companyId) => apiGet(`/companies/${companyId}/financial-years`);
export const createFinancialYear = (companyId, body) => apiPost(`/companies/${companyId}/financial-years`, body);
export const selectFinancialYear = (companyId, financialYearId) =>
  apiPost(`/companies/${companyId}/financial-year/select`, { financialYearId });
export const fetchCompanySettings = (companyId) => apiGet(`/companies/${companyId}/settings`);
export const updateCompanySettings = (companyId, body) => apiPut(`/companies/${companyId}/settings`, body);

// Measurements
export const listMeasurements = (search) => apiGet('/measurements', search ? { search } : {});
export const createMeasurements = (names) => apiPost('/measurements', { names });
export const updateMeasurement = (id, body) => apiPut(`/measurements/${id}`, body);
export const deleteMeasurement = (id) => apiDelete(`/measurements/${id}`);
export const reorderMeasurements = (order) => apiPut('/measurements/reorder', { order });
// Per-garment measurements captured on an order line
export const fetchInvoiceItemMeasurements = (itemId) => apiGet(`/invoices/items/${itemId}/measurements`);

// Tailoring Items
export const listItems = (params) => apiGet('/tailoring-items', params);
export const createItem = (body) => apiPost('/tailoring-items', body);
export const updateItem = (id, body) => apiPut(`/tailoring-items/${id}`, body);
export const duplicateItem = (id) => apiPost(`/tailoring-items/${id}/duplicate`);
export const deleteItem = (id) => {
  console.log('deleteItem called with id:', id);
  return apiDelete(`/tailoring-items/${id}`).then(result => {
    console.log('deleteItem success:', result);
    return result;
  }).catch(error => {
    console.error('deleteItem error:', error);
    throw error;
  });
};
export const fetchHsnSac = () => apiGet('/tailoring-items/hsn-sac');

// Customers
export const listCustomers = (params) => apiGet('/customers', params);
export const createCustomer = (body) => apiPost('/customers', body);
export const updateCustomer = (id, body) => apiPut(`/customers/${id}`, body);
export const deleteCustomer = (id) => apiDelete(`/customers/${id}`);
// A customer with orders is archived rather than deleted; this brings them back.
export const restoreCustomer = (id) => apiPost(`/customers/${id}/restore`, {});
export const fetchCustomer = (id) => apiGet(`/customers/${id}`);
export const importCustomers = (rows) => apiPost('/customers/import', { customers: rows });
export const fetchItem = (id) => apiGet(`/tailoring-items/${id}`);
// Whose numbers: leave familyMemberId out for the customer's own.
export const fetchCustomerMeasurements = (id, familyMemberId) =>
  apiGet(`/customers/${id}/measurements`, familyMemberId ? { familyMemberId } : undefined);

// Per-user UI choices, stored server-side so they follow the login.
export const fetchPreferences = () => apiGet('/auth/preferences');
export const savePreferences = (patch) => apiPatch('/auth/preferences', patch);
export const saveCustomerMeasurements = (id, rows, familyMemberId) =>
  apiPut(`/customers/${id}/measurements`, { rows, familyMemberId: familyMemberId || null });
export const fetchCustomerInvoices = (id) => apiGet(`/customers/${id}/invoices`);
export const fetchFamilyMembers = (id) => apiGet(`/customers/${id}/family-members`);
export const createFamilyMember = (id, body) => apiPost(`/customers/${id}/family-members`, body);

// Salesmen
export const listSalesmen = (params) => apiGet('/salesmen', params);
export const createSalesman = (body) => apiPost('/salesmen', body);
export const updateSalesman = (id, body) => apiPut(`/salesmen/${id}`, body);
export const deleteSalesman = (id) => apiDelete(`/salesmen/${id}`);

// Invoices
export const listInvoices = (params) => apiGet('/invoices', params);
export const fetchInvoiceOptions = () => apiGet('/invoices/options');
export const createInvoice = (body) => apiPost('/invoices', body);
// Is this invoice number free? Asked while typing, so a clash is seen early.
export const checkInvoiceNo = (orderNo, exceptId) => apiGet('/invoices/check-no', { orderNo, exceptId });
export const fetchInvoice = (id) => apiGet(`/invoices/${id}`);
export const updateInvoice = (id, body) => apiPut(`/invoices/${id}`, body);
// Status-only change — does not touch line items, payments or dates.
export const updateInvoiceStatus = (id, status) => apiPatch(`/invoices/${id}/status`, { status });
// Whether the customer's trial has happened — ticked from the order list.
export const updateInvoiceTrial = (id, done) => apiPatch(`/invoices/${id}/trial`, { done });
// Rescheduling the trial itself, the same way the delivery date is changed.
export const updateInvoiceTrialDate = (id, trialDate) => apiPatch(`/invoices/${id}/trial-date`, { trialDate });
// One garment's trial — the customer tries them on one at a time.
export const updateItemTrial = (itemId, done) => apiPatch(`/invoices/items/${itemId}/trial`, { done });
// A garment reaching the store: `leg` is 'trial' the first time and
// 'delivery' when it comes back to be collected.
export const updateItemReached = (itemId, leg, done) =>
  apiPatch(`/invoices/items/${itemId}/reached`, { leg, done });
// The whole order's garments at once, from the order list.
export const updateInvoiceReached = (id, leg, done) =>
  apiPatch(`/invoices/${id}/reached`, { leg, done });
// Reschedule only — leaves line items, payments and the other dates alone.
export const updateInvoiceDeliveryDate = (id, deliveryDate) =>
  apiPatch(`/invoices/${id}/delivery-date`, { deliveryDate });
// Orders are never destroyed: this moves one to the trash, and restore brings
// it back. `listInvoices({ trashed: 1 })` is the trash itself.
export const deleteInvoice = (id) => apiDelete(`/invoices/${id}`);
export const restoreInvoice = (id) => apiPost(`/invoices/${id}/restore`, {});
export const addPayment = (id, body) => apiPost(`/invoices/${id}/payments`, body);

// Work assignments (labour on order lines)
export const quoteAssignment = (params) => apiGet('/work-assignments/quote', params);
// Hand one job to one worker from the Workers screen.
export const assignWork = (body) => apiPost('/work-assignments', body);
export const updateAssignment = (id, body) => apiPatch(`/work-assignments/${id}`, body);

// Workers + their portal logins
export const listWorkers = (params) => apiGet('/workers', params);
// One worker, end to end: jobs, money, payouts and rate card.
export const fetchWorkerOverview = (id) => apiGet(`/workers/${id}/overview`);

// Supervisor's desk: the floor, the photos, the sign-offs.
export const supervisorBoard = () => apiGet('/supervisor/board');
export const supervisorWorkerJobs = (id) => apiGet(`/supervisor/workers/${id}/jobs`);
export const supervisorSetTrial = (invoiceId, done) => apiPatch(`/supervisor/invoices/${invoiceId}/trial`, { done });
export const supervisorComplete = (invoiceId) => apiPost(`/supervisor/invoices/${invoiceId}/complete`, {});
export const supervisorLoginAsWorker = (workerId) => apiPost(`/supervisor/workers/${workerId}/login-as`, {});
// Who is free and who is buried — for the allocation section.
export const fetchWorkerWorkload = () => apiGet('/workers/workload');
export const saveWorkerRate = (id, body) => apiPut(`/workers/${id}/rates`, body);
export const deleteWorkerRate = (id, rateId) => apiDelete(`/workers/${id}/rates/${rateId}`);
export const updateWorker = (id, body) => apiPut(`/workers/${id}`, body);

// What the shop floor may open when they sign in.
/** What the signed-in login may open — the one question a manager desk can ask too. */
export const fetchMyAccess = (companyId) => apiGet(`/companies/${companyId}/my-access`);
export const fetchPortalAccess = (companyId) => apiGet(`/companies/${companyId}/portal-access`);
export const savePortalAccess = (companyId, body) => apiPut(`/companies/${companyId}/portal-access`, body);
// Move one of your people into an account group — that group's switches are
// then the whole of what their login opens.
export const setWorkerGroup = (workerId, group) => apiPost(`/workers/${workerId}/group`, { group });
export const createWorker = (body) => apiPost('/workers', body);
export const createWorkerLogin = (id, body) => apiPost(`/workers/${id}/login`, body);
export const resetWorkerPassword = (id, password) => apiPut(`/workers/${id}/login`, { password });
export const revokeWorkerLogin = (id) => apiDelete(`/workers/${id}/login`);

// Settlements (admin finance)
export const settlementSummary = () => apiGet('/settlements/summary');
export const settlementPreview = (params) => apiGet('/settlements/preview', params);
export const postSettlement = (body) => apiPost('/settlements', body);
export const listSettlements = (params) => apiGet('/settlements', params);
export const fetchSettlement = (id) => apiGet(`/settlements/${id}`);
export const reverseSettlement = (id, reason) => apiPost(`/settlements/${id}/reverse`, { reason });
export const releaseSettlementLine = (id, lineId, reason) =>
  apiPost(`/settlements/${id}/lines/${lineId}/release`, { reason });

// Worker portal (WORKER-role logins only)
export const portalMe = () => apiGet('/worker-portal/me');
export const portalRates = () => apiGet('/worker-portal/rates');
export const portalOrders = (params) => apiGet('/worker-portal/orders', params);
export const portalOrder = (invoiceId) => apiGet(`/worker-portal/orders/${invoiceId}`);
// Completing a job sends the photos the worker just took: the supervisor
// inspects those, so the server refuses a completion without them.
export const portalSetStatus = (id, status, proof) =>
  apiPatch(`/worker-portal/assignments/${id}/status`, { status, ...(proof || {}) });
// A worker hands the garment on for checking once it is stitched or altered.
export const portalSetStage = (itemId, stage) => apiPatch(['/worker-portal/items/', itemId, '/stage'].join(''), { stage });
export const portalSettlements = () => apiGet('/worker-portal/settlements');

// Dashboard
export const dashboardBookings = (params) => apiGet('/dashboard/bookings', params);
export const dashboardUpcoming = (params) => apiGet('/dashboard/upcoming', params);
export const dashboardPending = (params) => apiGet('/dashboard/pending', params);
export const dashboardGeneral = (params) => apiGet('/dashboard/general', params);
// Owner's view: worker load, production time, delays, and where orders sit.
export const dashboardPerformance = (params) => apiGet('/dashboard/performance', params);
// The workshop floor: stage counts, and what is due today / tomorrow / overdue.
export const productionBoard = () => apiGet('/production/board');
// Move one garment to another stage by hand (QC, alteration, ready, delivered).
export const setItemStage = (itemId, body) => apiPatch(['/production/items/', itemId, '/stage'].join(''), body);
// Move a whole order on from the orders list (its least-advanced garments).
export const setOrderStage = (invoiceId, body) => apiPatch(['/production/invoices/', invoiceId, '/stage'].join(''), body);
// One order's life as a list of steps.
export const fetchInvoiceTimeline = (id) => apiGet(['/invoices/', id, '/timeline'].join(''));

// Reports
export const fetchReportCatalogue = () => apiGet('/reports/catalogue');
export const runReport = (params) => apiGet('/reports', params);

// Custom Work Rates
export const listWorkRates = () => apiGet('/work-rates');
export const createWorkRate = (body) => apiPost('/work-rates', body);
export const attachWorkRateToItem = (itemId, workRateId) => apiPost(`/tailoring-items/${itemId}/work-rates`, { workRateId });
export const detachWorkRateFromItem = (itemId, workRateId) => apiDelete(`/tailoring-items/${itemId}/work-rates/${workRateId}`);

// Uploads
export const uploadFile = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const { default: API } = await import('./client');
  const res = await API.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data;
};